import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export interface PermissionOut {
  id: string;
  name: string;
  resource: string | null;
  action: string | null;
  description: string | null;
}

export interface RoleOut {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  created_at: string;
}

export interface RoleDetailOut extends RoleOut {
  permissions: PermissionOut[];
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}`;
}

export function useRoles() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId,
    queryKey: ['roles', orgId],
    queryFn: async () => {
      const { data } = await clientApi.get<RoleOut[]>(`${orgUrl(orgId!)}/roles`);
      return data;
    },
  });
}

export function useRole(roleId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!roleId,
    queryKey: ['roles', orgId, roleId],
    queryFn: async () => {
      const { data } = await clientApi.get<RoleDetailOut>(`${orgUrl(orgId!)}/roles/${roleId}`);
      return data;
    },
  });
}

export function useCreateRole() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { name: string; description?: string }) => {
      const { data } = await clientApi.post<RoleOut>(`${orgUrl(orgId!)}/roles`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', orgId] });
    },
  });
}

export function useUpdateRole() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; description?: string | null }) => {
      const { data } = await clientApi.patch<RoleOut>(`${orgUrl(orgId!)}/roles/${id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', orgId] });
    },
  });
}

export function useDeleteRole() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/roles/${roleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', orgId] });
    },
  });
}

export function useSetRolePermissions() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, permissionIds }: { roleId: string; permissionIds: string[] }) => {
      const { data } = await clientApi.put<RoleDetailOut>(
        `${orgUrl(orgId!)}/roles/${roleId}/permissions`,
        { permission_ids: permissionIds },
      );
      return data;
    },
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: ['roles', orgId] });
      queryClient.invalidateQueries({ queryKey: ['roles', orgId, roleId] });
    },
  });
}

export function usePermissions() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId,
    queryKey: ['permissions', orgId],
    queryFn: async () => {
      const { data } = await clientApi.get<PermissionOut[]>(`${orgUrl(orgId!)}/permissions`);
      return data;
    },
  });
}
